import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { useAppSelector } from './redux/hooks';
import SidebarLayout from './layouts/SidebarLayout';
import PageNotFound from './components/PageNotFound';
import EmployeesListing from './pages/employees/employees-listing';
import WarehouseListing from './pages/warehouse/warehouse-listing';
import SupplierListing from './pages/Supplier/SupplierListing';
import StorageUnitListing from './pages/StorageUnit/storage-unit-listing';
import StorageUnitDetailPage from './pages/StorageUnit/StorageUnitDetailPage';
import WarehouseDetail from './pages/warehouse/WarehouseDetail';
import ShipmentListing from './pages/shipment/shipment-listing';
import ShipmentDetailPage from './pages/shipment/shipment-details';
import ProductDetailPage from './pages/products/ProductDetailPage';
import RequirePermission from 'src/components/RequirePermission';
import PermissionService, { ACTION, ROLE } from './services/permission-service';
import {
  Warehouse as WarehouseIcon,
  People as PeopleIcon,
  Inventory as ProductIcon,
  LocalShipping as ShipmentIcon,
  Business as SupplierIcon,
  Storage as StorageIcon,
  Category as TypeIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Receipt as OrderIcon,
  Autorenew,
  Undo,
  LocalShipping,
  Print as PrintIcon,
} from '@mui/icons-material';
import TypesListing from './pages/Types/types-listing';
import AuditDashboard from './pages/Audits/AuditDashboard';
import ClientListing from './pages/Clients/Clients-listing';
import ProductAnalyticsPage from './pages/products/ProductAnalaticsPage';
import BoxManagementPage from './pages/BoxManagement/BoxDetailPage';
import NewReallocationPage from './pages/Movement/ReallocationPage';
import NewMoveInPage from './pages/Movement/NewMoveInPage';
import NewMoveOutPage from './pages/Movement/NewMoveOutPage';
import OperationsListPage from './pages/Movement/OperationsListPage';
import OperationDetailsPage from './pages/Movement/OperationDetailsPage';
import { TransferType } from './types/Operation.types';
import UnitListingSimple from './pages/Units/Units-listing';
import UnknownUnitsListing from './pages/Units/unsynced-units-listing';
import OnHandListing from './pages/Units/on-hand-listing';
import { BoxIcon } from 'lucide-react';
import BoxListing from './pages/BoxManagement/Box-listing';
import InactiveUnitsListing from './pages/Units/Inactive-units-listing';
import TransferOrderListing from './pages/TransferOrder/TransferOrderListing';
import CreateTransferOrder from './pages/TransferOrder/CreateTransferOrder';
import TransferOrderDetail from './pages/TransferOrder/TransferOrderDetail';
import AllocateOrder from './pages/TransferOrder/AllocateOrder';
import PickOrder from './pages/TransferOrder/PickOrder';
import ShipOrder from './pages/TransferOrder/ShipOrder';
import ReceiveOrder from './pages/Client/ReceiveOrder';
import ClientOnHand from './pages/Clients/ClientOnHand';
import ClientReception from './pages/Clients/ClientReception';
import ClientReceptionValidation from './pages/Clients/ClientReceptionValidation';
import OrderReceivingList from './pages/order-receiving/order-receiving-list';
import OrderReceivingDetails from './pages/order-receiving/order-receiving-details';
import Dashboard from './pages/Dashboard/dashboard';
import PrintBarcodes from './pages/PrintBarcodes/PrintBarcodes';

interface RouteConfig {
  path: string;
  element: React.ReactElement;
  title: string;
  permission?: ACTION;
  exact?: boolean;
}

const routeConfigs: RouteConfig[] = [
  {
    path: '/',
    element: <Dashboard />,
    title: 'Dashboard',
    exact: true
  },
  {
    path: '/users',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_EMPLOYEES}>
        <EmployeesListing />
      </RequirePermission>
    ),
    title: 'Employee Management',
    permission: ACTION.VIEW_ALL_EMPLOYEES
  },
  {
    path: '/warehouse',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_WAREHOUSES}>
        <WarehouseListing />
      </RequirePermission>
    ),
    title: 'Warehouse Management',
    permission: ACTION.VIEW_ALL_WAREHOUSES,
    exact: true
  },
  { 
    path: '/warehouse/:id', 
    element: <WarehouseDetail />, 
    title: 'Warehouse Details' 
  },
  {
    path: '/storage-unit',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_STORAGE_UNITS}>
        <StorageUnitListing />
      </RequirePermission>
    ),
    title: 'Storage Unit Management',
    permission: ACTION.VIEW_ALL_STORAGE_UNITS,
    exact: true
  },
  { 
    path: '/storage-unit/:id', 
    element: <StorageUnitDetailPage />, 
    title: 'Storage Unit Details' 
  },
  
  { 
    path: '/Units', 
    element: <UnitListingSimple />, 
    title: 'Units Management', 
    exact: true 
  },
  { 
    path: '/on_hand', 
    element: <OnHandListing />, 
    title: 'On Hand', 
    exact: true 
  },
  { 
    path: '/unknown-units', 
    element: <UnknownUnitsListing />, 
    title: 'Unknown Units', 
    exact: true 
  },
    { 
    path: '/inactive-units', 
    element: <InactiveUnitsListing />, 
    title: 'Inactive Units', 
    exact: true 
  },
    { 
    path: '/boxes', 
    element: <BoxListing />, 
    title: 'Boxes', 
    exact: true 
  },
  { 
    path: '/Units/:id', 
    element: <ProductDetailPage />, 
    title: 'Product Details' 
  },
  { 
    path: '/analytics/product/:id', 
    element: <ProductAnalyticsPage />, 
    title: 'Product Details' 
  },
  { 
    path: '/box/:boxBarcode', 
    element: <BoxManagementPage />, 
    title: 'Box Details' 
  },
  {
    path: '/movements/all',
    element: <OperationsListPage
      initialTransferType={null}
      showTypeFilter={false}
      title="All Operations"
      exportEndpoint="api/Operation/export"
    />,
    title: 'All Operations'
  },
  {
    path: '/movements/reallocation',
    element: <OperationsListPage
      initialTransferType={TransferType.Reallocation}
      showTypeFilter={false}
      showCreateButton={true}
      createPath="/movements/new-reallocation"
      title="Reallocation Operations"
      exportEndpoint="api/Operation/export?type=3"
    />,
    title: 'Reallocation Operations'
  },
  {
    path: '/movements/new-reallocation',
    element: <NewReallocationPage />,
    title: 'New Reallocation'
  },
  {
    path: '/movements/move-in',
    element: <OperationsListPage
      initialTransferType={TransferType.MoveIn}
      showTypeFilter={false}
      showCreateButton={true}
      createPath="/movements/new-move-in"
      title="Move In Operations"
      exportEndpoint="api/Operation/export?type=1"
    />,
    title: 'Move In Operations'
  },
  {
    path: '/transfer-order',
    element: <TransferOrderListing />,
    title: 'Transfer Order Management',
  },
  {
    path: '/new-transfer-order',
    element: <CreateTransferOrder />,
    title: 'Create Transfer Order',
  },
  {
    path: '/transfer-orders/:id',
    element: <TransferOrderDetail />,
    title: 'Transfer Order Details',
  },
  {
    path: '/transfer-orders/:id/allocate',
    element: <AllocateOrder />,
    title: 'Allocate Transfer Order',
  },
  {
    path: '/transfer-orders/:id/pick',
    element: <PickOrder />,
    title: 'Pick Transfer Order',
  },
  {
    path: '/transfer-orders/:id/ship',
    element: <ShipOrder />,
    title: 'Ship Transfer Order',
  },
  {
    path: '/client/receive-order/:id',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <ReceiveOrder />
      </RequirePermission>
    ),
    title: 'Receive Order',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/client/orders',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <TransferOrderListing />
      </RequirePermission>
    ),
    title: 'My Orders',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/order-receiving',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <OrderReceivingList />
      </RequirePermission>
    ),
    title: 'Order Receiving',
    permission: ACTION.SEE_CLIENT_ON_HAND,
    exact: true
  },
  {
    path: '/order-receiving/:id',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <OrderReceivingDetails />
      </RequirePermission>
    ),
    title: 'Order Receiving Details',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/movements/new-move-in',
    element: <NewMoveInPage />,
    title: 'New Move In'
  },
  {
    path: '/movements/move-out',
    element: <OperationsListPage
      initialTransferType={TransferType.MoveOut}
      showTypeFilter={false}
      showCreateButton={true}
      createPath="/movements/new-move-out"
      title="Move Out Operations"
      exportEndpoint="api/Operation/export?type=2"
    />,
    title: 'Move Out Operations'
  },
  {
    path: '/movements/new-move-out',
    element: <NewMoveOutPage />,
    title: 'New Move Out'
  },
  {
    path: '/movements/operation/:operationId',
    element: <OperationDetailsPage />,
    title: 'Operation Details'
  },
  { 
    path: '/types', 
    element: <TypesListing />, 
    title: 'Product Types Management', 
    exact: true 
  },
  
  {
    path: '/suppliers',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_SUPPLIERS}>
        <SupplierListing />
      </RequirePermission>
    ),
    title: 'Supplier Management',
    permission: ACTION.VIEW_ALL_SUPPLIERS
  },
    {
    path: '/clients',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_CLIENTS}>
        <ClientListing />
      </RequirePermission>
    ),
    title: 'Supplier Management',
    permission: ACTION.VIEW_ALL_SUPPLIERS
  },
  {
    path: '/client-on-hand/:clientId',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <ClientOnHand />
      </RequirePermission>
    ),
    title: 'Client On Hand',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/client/:clientId/reception',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <ClientReception />
      </RequirePermission>
    ),
    title: 'Client Reception',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/client/:clientId/reception/validate',
    element: (
      <RequirePermission action={ACTION.SEE_CLIENT_ON_HAND}>
        <ClientReceptionValidation />
      </RequirePermission>
    ),
    title: 'Validate Client Reception',
    permission: ACTION.SEE_CLIENT_ON_HAND
  },
  {
    path: '/shipments',
    element: <ShipmentListing />,
    title: 'Shipment Management',
    exact: true
  },
  { 
    path: '/shipments/:refNumber', 
    element: <ShipmentDetailPage />, 
    title: 'Shipment Details' 
  },
  
  {
    path: '/audit-dashboard',
    element: <AuditDashboard />,
    title: 'Audit Dashboard',
    exact: true
  },
  {
    path: '/print-barcodes',
    element: <PrintBarcodes />,
    title: 'Print Barcodes',
    exact: true
  },
];

interface SidebarCategory {
  id: string;
  label: string;
  order: number;
}

const SIDEBAR_CATEGORIES: SidebarCategory[] = [
  { id: 'dashboard', label: 'Dashboard', order: 1 },
  { id: 'operations', label: 'Operations', order: 2 },
  { id: 'inventory', label: 'Inventory', order: 3 },
  { id: 'partners', label: 'Business Partners', order: 4 },
  // { id: 'analytics', label: 'Analytics & Reports', order: 5 },
  { id: 'administration', label: 'Administration', order: 6 },
];

export const Protected: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.user);
  const isAuthed = user.isAuthenticated;

  useEffect(() => {
    if (!isAuthed) {
      navigate('/login', { replace: true });
    }
  }, [isAuthed, navigate]);

const sidebarLinks = useMemo(() => {
  const role = user.user?.role as ROLE;
  const perm = PermissionService.getInstance();

  // If user is a client, show only Client On Hand and Orders
  if (role === ROLE.CLIENT) {
    return [
            {
        id: 'orders',
        icon: <OrderIcon />,
        label: 'Transfer Orders',
        visible: true,
        category: 'operations',
        order: 1,
        children: [
          // {
          //   id: 'my_orders',
          //   icon: <OrderIcon />,
          //   label: 'My Orders',
          //   route: '/client/orders',
          //   visible: true
          // },
          {
            id: 'order_receiving',
            icon: <LocalShipping />,
            label: 'Order Receiving',
            route: '/order-receiving',
            visible: true
          },
        ]
      },
      {
        id: 'inventory',
        icon: <ProductIcon />,
        label: 'Inventory',
        visible: true,
        category: 'inventory',
        order: 2,
        children: [
           {
            id: 'client_on_hand',
            icon: <ProductIcon />,
            label: 'Inventory',
            route: `/client-on-hand/${user.user?.id}`,
            visible: true
          },
          {
            id: 'client_reception',
            icon: <ProductIcon />,
            label: 'Reception',
            route: `/client/${user.user?.id}/reception`,
            visible: true
          },
          // {
          //   id: 'client-boxes',
          //   icon: <BoxIcon />,
          //   label: 'Boxes',
          //   route: '/boxes',
          //   visible: true
          // }
        ]
      },
    ];
  }

  const links = [
    {
      id: 'dashboard',
      icon: <DashboardIcon />,
      label: 'Dashboard',
      route: '/',
      visible: true,
      category: 'dashboard',
      order: 1
    },

    {
      id: 'operations',
      icon: <WarehouseIcon />,
      label: 'Operations',
      visible: true,
      category: 'operations',
      order: 1,
      children: [
        {
          id: 'shipments',
          icon: <ShipmentIcon />,
          label: 'Shipments',
          route: '/shipments',
          visible: true
        },
        {
          id: 'Movements',
          icon: <OrderIcon />,
          label: 'All Movements',
          route: '/movements/all',
          visible: true
        },
        {
          id: 'Reallocations',
          icon: <Autorenew />,
          label: 'Reallocation',
          route: '/movements/reallocation',
          visible: true
        },
        {
          id: 'move-in',
          icon: <Undo />,
          label: 'Move In',
          route: '/movements/move-in',
          visible: true
        },
        {
          id: 'transfer-order',
          icon: <OrderIcon />,
          label: 'Transfer Orders',
          route: '/transfer-order',
          visible: true
        },
        {
          id: 'move-out',
          icon: <Undo />,
          label: 'Move Out',
          route: '/movements/move-out',
          visible: true
        }
      ]
    },

    {
      id: 'inventory',
      icon: <ProductIcon />,
      label: 'Inventory',
      visible: true,
      category: 'inventory',
      order: 1,
      children: [
        {
          id: 'Units',
          icon: <ProductIcon />,
          label: 'Units',
          route: '/units',
          visible: true
        },
        {
          id: 'on_hand',
          icon: <ProductIcon />,
          label: 'On Hand',
          route: '/on_hand',
          visible: true
        },
        {
          id: 'Unkown Units',
          icon: <ProductIcon />,
          label: 'Unknown Units',
          route: '/unknown-units',
          visible: true
        },
        {
          id: 'Inactive Units',
          icon: <ProductIcon />,
          label: 'Inactive Units',
          route: '/inactive-units',
          visible: true
        },
        {
          id: 'Boxes',
          icon: <BoxIcon />,
          label: 'Boxes',
          route: '/boxes',
          visible: true
        },
        perm.isAllowed(ACTION.VIEW_ALL_WAREHOUSES, role) && {
          id: 'warehouse',
          icon: <WarehouseIcon />,
          label: 'Warehouses',
          route: '/warehouse',
          visible: true
        },
        perm.isAllowed(ACTION.VIEW_ALL_STORAGE_UNITS, role) && {
          id: 'storage-unit',
          icon: <StorageIcon />,
          label: 'Storage Units',
          route: '/storage-unit',
          visible: true
        },
        {
          id: 'types',
          icon: <TypeIcon />,
          label: 'Types',
          route: '/types',
          visible: true
        },
      ].filter(Boolean)
    },

    {
      id: 'partners',
      icon: <SupplierIcon />,
      label: 'Business Partners',
      visible: true,
      category: 'partners',
      order: 1,
      children: [
        perm.isAllowed(ACTION.VIEW_ALL_SUPPLIERS, role) && {
          id: 'suppliers',
          icon: <SupplierIcon />,
          label: 'Suppliers',
          route: '/suppliers',
          visible: true
        },
        perm.isAllowed(ACTION.VIEW_ALL_CLIENTS, role) && {
          id: 'clients',
          icon: <SupplierIcon />,
          label: 'Clients',
          route: '/clients',
          visible: true
        },
      ].filter(Boolean)
    },

    // Analytics & Reports
    // {
    //   id: 'analytics',
    //   icon: <ReportsIcon />,
    //   label: 'Reports',
    //   visible: true,
    //   category: 'analytics',
    //   order: 1,
    //   children: [
    //     {
    //       id: 'audit-dashboard',
    //       icon: <AuditIcon />,
    //       label: 'Audit Reports',
    //       route: '/audit-dashboard',
    //       visible: true
    //     }
    //   ]
    // },

    // Administration
    perm.isAllowed(ACTION.VIEW_ALL_EMPLOYEES, role) && {
      id: 'administration',
      icon: <SecurityIcon />,
      label: 'Administration',
      visible: true,
      category: 'administration',
      order: 1,
      children: [
        {
          id: 'employees',
          icon: <PeopleIcon />,
          label: 'Users',
          route: '/users',
          visible: true
        },
        {
          id: 'print-barcodes',
          icon: <PrintIcon />,
          label: 'Print Barcodes',
          route: '/print-barcodes',
          visible: true
        }
      ]
    }
  ].filter(Boolean);

  // sort by category order then link order
  return links.sort((a, b) => {
    const catA = SIDEBAR_CATEGORIES.find(c => c.id === a.category)?.order ?? 999;
    const catB = SIDEBAR_CATEGORIES.find(c => c.id === b.category)?.order ?? 999;
    if (catA !== catB) return catA - catB;
    return (a.order ?? 999) - (b.order ?? 999);
  });
}, [user.user?.role]);


  const layoutConfig = useMemo(() => ({
    sidebarWidth: { collapsed: 72, expanded: 300 },
    headerHeight: 64,
    showTabs: true,
    showUserInfo: true,
    allowSidebarToggle: true,
    showSearchBar: false,
    showNotifications: false, 
    theme: "dark" as "dark",
  }), []);

  const enhancedUser = useMemo(() => ({
    name: user.user?.name || 'Unknown User',
    email: user.user?.email || '',
    role: user.user?.role || 'user',
    initials: user.user?.name 
      ? user.user.name.split(' ').map(n => n[0]).join('').toUpperCase() 
      : 'UN',
  }), [user.user]);

  if (!isAuthed) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <SidebarLayout
            links={sidebarLinks}
            user={enhancedUser}
            config={layoutConfig}
          />
        }
      >
        {routeConfigs.map((routeConfig, index) => (
          <Route 
            key={`${routeConfig.path}-${index}`} 
            path={routeConfig.path} 
            element={routeConfig.element} 
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};